import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '@Sage/hoi-poi-ui';
import { UserChip } from 'containers/components/chips';
import RowDetailWidget from 'containers/components/widgets/components/RowDetailWidget';
import FeaturedFieldsList from 'containers/components/widgets/components/FeaturedFields/FeaturedFieldsList';
import { useFeaturedFields } from 'containers/components/widgets/components/FeaturedFields/hooks/useFeaturedFields';
import { withEmailEditor } from 'containers/components/EmailEditor/hooks/useEmailEditor';
import MoreFieldsModal from 'containers/components/widgets/components/FeaturedFields/MoreFieldsModal';
import CompanyStatusSelector from './CompanyStatusSelector';
import { COMPANIES } from 'constants/Entities';
import { validUrl } from 'utils/links/checkUrls';
import TruncatedTooltip from 'components/TruncatedTooltip';
import TruncatedLines from 'components/TruncatedLines';
import { MarketingStatusDetailField } from 'components/TableCells';
import { getLiteral } from 'utils/getLiteral';

const propTypes = {
    canConfigImplementation: PropTypes.bool,
    company: PropTypes.object,
    extraFieldsSchemaMap: PropTypes.object,
    productModeCode: PropTypes.string,
    showFax: PropTypes.bool,
    showVatNumber: PropTypes.bool,
    standardFieldsConfiguration: PropTypes.object,
};

const mapStateToProps = (state) => ({
    canConfigImplementation: state.config.permission.canConfigImplementation,
    extraFieldsSchemaMap: state.config.extraFieldsSchemaMap || {},
    productModeCode: state.config.productModeCode,
    showFax: state.config.permission.ShowFAX || false,
    showVatNumber: state.config.permission.ShowVATNumber || false,
    standardFieldsConfiguration:
        state.config.standardFieldsSchemaMap[COMPANIES.extraFieldName] || null,
    useTaxesSalesOrders: state.config.userData?.useTaxesSalesOrders || false,
    marketingCampaignsEnabled: state.config.userData?.marketingCampaigns,
});

function useFieldVisibility(
    standardFieldsConfiguration,
    company,
    showFax,
    showVatNumber,
    useTaxesSalesOrders,
    marketingCampaignsEnabled,
) {
    return useMemo(() => {
        const cfg = standardFieldsConfiguration;
        const showState = cfg?.idestadoempresa ? !cfg.idestadoempresa.isHidden : true;
        const showEnvironment = cfg?.idsucursal ? !cfg.idsucursal.isHidden : true;
        const showResponsible = cfg?.idComercial ? !cfg.idComercial.isHidden : true;
        const showResponsible2 = cfg?.idComercial2 ? !cfg.idComercial2.isHidden : true;
        const showResponsible3 = cfg?.idComercial3 ? !cfg.idComercial3.isHidden : true;
        const showResponsible4 = cfg?.idComercial4 ? !cfg.idComercial4.isHidden : true;
        const showResponsible5 = cfg?.idComercial5 ? !cfg.idComercial5.isHidden : true;
        const showResponsibles =
            showResponsible ||
            showResponsible2 ||
            showResponsible3 ||
            showResponsible4 ||
            showResponsible5;
        const showRate = cfg?.idtarifa ? !cfg.idtarifa.isHidden : true;
        const showNif = cfg?.nif ? !cfg.nif.isHidden : true;
        const showWeb = cfg?.web ? !cfg.web.isHidden : true;
        const showPhone = cfg?.tel ? !cfg.tel.isHidden : true;
        const showPhone2 = cfg?.tel2 ? !cfg.tel2.isHidden : true;
        const showCellPhone = cfg?.strmovil ? !cfg.strmovil.isHidden : true;
        const showPhones = showPhone || showPhone2 || showCellPhone;
        const showEmail = cfg?.email ? !cfg.email.isHidden : true;
        const showFaxConfig = cfg?.fax ? !cfg.fax.isHidden : true;
        const marketingCfg = cfg?.marketingStatus;
        const showMarketingStatus =
            marketingCampaignsEnabled && (marketingCfg == null || !marketingCfg.isHidden);

        return {
            showState: showState && company?.canShowStateField,
            showEnvironment,
            showResponsibles,
            showRate: showRate && company?.canShowRateField,
            showNif: showNif && showVatNumber,
            showWeb,
            showPhones,
            showEmail,
            showFax: showFaxConfig && showFax,
            showTaxProfile: useTaxesSalesOrders,
            showMarketingStatus,
        };
    }, [
        standardFieldsConfiguration,
        company,
        showFax,
        showVatNumber,
        useTaxesSalesOrders,
        marketingCampaignsEnabled,
    ]);
}

const CompanyDetailWidgetContent = memo(function CompanyDetailWidgetContent({
    canConfigImplementation,
    canUseEmail,
    company,
    extraFieldsSchemaMap,
    mailTo,
    productModeCode,
    showFax,
    showVatNumber,
    smartFieldProps,
    standardFieldsConfiguration,
    marketingCampaignsEnabled,
    useTaxesSalesOrders,
}) {
    const visibility = useFieldVisibility(
        standardFieldsConfiguration,
        company,
        showFax,
        showVatNumber,
        useTaxesSalesOrders,
        marketingCampaignsEnabled,
    );

    const {
        allExtraFields,
        featuredFieldsRef,
        featuredIds,
        finalSections,
        isLoading,
        isModalOpen,
        onCancel,
        onChangeFeatured,
        onClickInput,
        onConfirm,
        openModal,
    } = useFeaturedFields({
        data: company,
        entity: COMPANIES,
        entityExtrafields: company?.extraFields,
        entityId: company?.id,
        extraFieldsSchemaMap,
        setDynamicAsHidden: undefined,
    });

    const showResponsibleField =
        (company?.idResponsable ||
            company?.idResponsable2 ||
            company?.idResponsable3 ||
            company?.idResponsable4 ||
            company?.idResponsable5) &&
        visibility.showResponsibles;

    const showSection =
        company?.web ||
        company?.phone ||
        company?.phone2 ||
        company?.cellPhone ||
        company?.email1 ||
        company?.email2 ||
        company?.email3 ||
        (visibility.showFax && company?.fax);

    const renderOwnerChip = useCallback((id, name, number, isOnlyOne) => {
        const classes = `company-info-widget-content__responsible__responsible responsible${number}`;
        if (isOnlyOne) {
            return (
                <div key={id} className={classes}>
                    <UserChip idUser={id} remotePopover username={name} />
                </div>
            );
        }
        return (
            <div key={id} className={classes}>
                <UserChip idUser={id} remotePopover onlyImage />
            </div>
        );
    }, []);

    const renderOwnersInfo = useCallback(() => {
        const owners = [];
        if (company?.idResponsable)
            owners.push({
                id: company.idResponsable,
                name: company.responsible,
                number: 1,
            });
        if (company?.idResponsable2)
            owners.push({
                id: company.idResponsable2,
                name: company.responsible2,
                number: 2,
            });
        if (company?.idResponsable3)
            owners.push({
                id: company.idResponsable3,
                name: company.responsible3,
                number: 3,
            });
        if (company?.idResponsable4)
            owners.push({
                id: company.idResponsable4,
                name: company.responsible4,
                number: 4,
            });
        if (company?.idResponsable5)
            owners.push({
                id: company.idResponsable5,
                name: company.responsible5,
                number: 5,
            });
        if (owners.length === 0) return '-';
        if (owners.length === 1)
            return renderOwnerChip(owners[0].id, owners[0].name, owners[0].number, true);
        return owners.map((o) => renderOwnerChip(o.id, o.name, o.number, false));
    }, [company, renderOwnerChip]);

    const renderWeb = useCallback(() => {
        if (!company?.web) return '-';
        const url = validUrl(company.web);
        return (
            <a href={url} rel="noreferrer" target="_blank">
                {company.web}
            </a>
        );
    }, [company?.web]);

    const handleClickEmail = useCallback(
        (event, email) => {
            if (canUseEmail) {
                event.preventDefault();
                mailTo(email, true);
            }
        },
        [canUseEmail, mailTo],
    );

    const renderPhones = useCallback(
        () => (
            <Fragment>
                <div className="company-info-widget-content__phones__phone company-phone">
                    {company?.phone ? <a href={`tel:${company.phone}`}>{company.phone}</a> : '-'}
                </div>
                {company?.phone2 && (
                    <div className="company-info-widget-content__phones__phone company-phone2">
                        <a href={`tel:${company.phone2}`}>{company.phone2}</a>
                    </div>
                )}
                {company?.cellPhone && (
                    <div className="company-info-widget-content__phones__phone company-cellphone">
                        <a href={`tel:${company.cellPhone}`}>{company.cellPhone}</a>
                    </div>
                )}
            </Fragment>
        ),
        [company?.phone, company?.phone2, company?.cellPhone],
    );

    const renderEmail = useCallback(
        () => (
            <Fragment>
                <TruncatedTooltip content={company?.email1}>
                    {company?.email1 ? (
                        <a
                            href={`mailto:${company.email1}`}
                            onClick={(e) => handleClickEmail(e, company.email1)}
                        >
                            {company.email1}
                        </a>
                    ) : (
                        '-'
                    )}
                </TruncatedTooltip>
                {company?.email2 && (
                    <TruncatedTooltip content={company.email2}>
                        <a
                            href={`mailto:${company.email2}`}
                            onClick={(e) => handleClickEmail(e, company.email2)}
                        >
                            {company.email2}
                        </a>
                    </TruncatedTooltip>
                )}
                {company?.email3 && (
                    <TruncatedTooltip content={company.email3}>
                        <a
                            href={`mailto:${company.email3}`}
                            onClick={(e) => handleClickEmail(e, company.email3)}
                        >
                            {company.email3}
                        </a>
                    </TruncatedTooltip>
                )}
            </Fragment>
        ),
        [company?.email1, company?.email2, company?.email3, handleClickEmail],
    );

    const showSeeAllButton = !isLoading && !!allExtraFields?.length;

    return (
        <>
            <div className="company-info-widget-content">
                <div className="company-info-widget-content__block">
                    {visibility.showState && (
                        <RowDetailWidget
                            className="company-info-widget-content__status"
                            label="label_status"
                            value={
                                <div className="company-info-widget-content__status">
                                    <CompanyStatusSelector
                                        isReadOnly={smartFieldProps?.idCompanyState?.readOnly}
                                    />
                                </div>
                            }
                        />
                    )}
                    {visibility.showEnvironment && (
                        <RowDetailWidget
                            className="company-info-widget-content__environment"
                            label="label_environment"
                            value={<TruncatedLines text={company?.environment} />}
                        />
                    )}
                    {showResponsibleField && (
                        <RowDetailWidget
                            className="company-info-widget-content__responsible"
                            label="label_responsible"
                            value={renderOwnersInfo()}
                        />
                    )}
                    {visibility.showRate && company?.tarifa && (
                        <RowDetailWidget
                            className="company-info-widget-content__rate"
                            label="label_rate"
                            value={<TruncatedLines text={company.tarifa} />}
                        />
                    )}
                    {visibility.showNif && company?.nif && (
                        <RowDetailWidget
                            className="company-info-widget-content__nif"
                            label="label_vat_number"
                            value={company.nif}
                        />
                    )}
                    {visibility.showTaxProfile && company?.taxProfile && (
                        <RowDetailWidget
                            className="company-info-widget-content__tax-profile"
                            label="label_accounts_tax_profile"
                            value={company.taxProfile}
                        />
                    )}
                </div>
                {showSection && (
                    <div className="company-info-widget-content__block">
                        {visibility.showWeb && (
                            <RowDetailWidget
                                className="company-info-widget-content__web"
                                label="label_web"
                                value={renderWeb()}
                                withTooltip={company?.web}
                            />
                        )}
                        {visibility.showPhones && (
                            <RowDetailWidget
                                className="company-info-widget-content__phones"
                                label="label_phone_numbers"
                                value={renderPhones()}
                            />
                        )}
                        {visibility.showEmail && (
                            <RowDetailWidget
                                className="company-info-widget-content__email"
                                label="label_email"
                                value={renderEmail()}
                                withTooltip
                            />
                        )}
                        {visibility.showFax && company?.fax && (
                            <RowDetailWidget
                                className="company-info-widget-content__fax"
                                label="label_fax"
                                value={<TruncatedLines text={company.fax} />}
                            />
                        )}
                    </div>
                )}
                {visibility.showMarketingStatus && (
                    <RowDetailWidget
                        className="company-info-widget-content__marketing-status"
                        label="label_marketing_status"
                        value={<MarketingStatusDetailField entity={COMPANIES} record={company} />}
                    />
                )}
                <FeaturedFieldsList
                    allExtraFields={allExtraFields}
                    canConfigImplementation={canConfigImplementation}
                    entity={COMPANIES}
                    finalSections={finalSections}
                    isLoading={isLoading}
                    productModeCode={productModeCode}
                />
            </div>
            {showSeeAllButton && (
                <div className="company-info-widget-footer">
                    <Button type="secondary" onClick={openModal}>
                        {getLiteral('action_see_all_customfields')}
                    </Button>
                </div>
            )}
            <MoreFieldsModal
                entityName={COMPANIES?.trueName}
                featuredFields={featuredFieldsRef.current}
                featuredIds={featuredIds}
                isLoading={isLoading}
                isOpen={isModalOpen}
                onCancel={onCancel}
                onChangeFeatured={onChangeFeatured}
                onClickInput={onClickInput}
                onConfirm={onConfirm}
                sections={finalSections}
            />
        </>
    );
});

CompanyDetailWidgetContent.propTypes = propTypes;

export default connect(mapStateToProps)(withEmailEditor(CompanyDetailWidgetContent));
